<script lang="ts" setup>
import OmniTable from "@/components/omni/OmniTable.vue";
import CheckCallDateTime from "@/views/trips/view/components/CheckCallDateTime.vue";
import { FilterUtils } from "@/services/filters";
import { useDateTime } from "@/composables";
import { useAppStore } from "@/store/app.store";
import DismissedChip from "@/components/chips/DismissedChip.vue";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import TruckLink from "@/components/links/TruckLink.vue";
import type { CheckCallWithUpdate } from "@/api/trips/dto/check-call";
import type { TravelOrderWithTruck } from "@/api/trips/dto/trip.dto";

export type CheckCallTruckInfo = {
  truckId: number | undefined;
  truckNumber: string;
  travelOrderId: number;
  orderNumber: number | undefined;
};

const filters = new FilterUtils();
const { dateTZ } = useDateTime();
const { dispatcher } = useAppStore();
const { t } = useI18n();

const props = defineProps<{
  checkCalls: CheckCallWithUpdate[];
  resolveTravelOrder: (checkCall: CheckCallWithUpdate) => TravelOrderWithTruck | undefined;
  isCreateDisabled: (checkCall: CheckCallWithUpdate) => boolean;
  resolveTruckInfo?: (checkCall: CheckCallWithUpdate) => CheckCallTruckInfo | undefined;
}>();

const tableHeaders = computed(() => {
  const headers: Record<string, unknown>[] = [
    { title: "Type", key: "type", sortable: false },
    { title: "Location", key: "locationAddress", class: "text-grey", sortable: false },
    { title: "Broker update", key: "brokerUpdate", sortable: false },
    { title: "Date time", key: "dateTime", sortable: false },
    { title: "Actions", key: "actions", sortable: false, align: "center" },
  ];

  if (props.resolveTruckInfo) {
    headers.splice(1, 0, { title: "Truck number", key: "truckNumber", sortable: false });
  }

  return headers;
});

const emits = defineEmits<{
  (e: "viewUpdate", checkCall: CheckCallWithUpdate): void;
  (e: "openCreateUpdate", travelOrder: TravelOrderWithTruck, checkCall: CheckCallWithUpdate): void;
}>();

function getRowClass(row: Record<string, string> & { item: CheckCallWithUpdate }): string {
  if (row.item.deletedBy) {
    return "bg-grey-lighten-2";
  }

  return "";
}

function rowProps(row: Record<string, string> & { item: CheckCallWithUpdate }): Record<string, string> {
  return {
    class: getRowClass(row),
  };
}

function getTime(createAt: string, checkCallTz: string = "") {
  return dateTZ(createAt || "", checkCallTz, "HH:mm");
}

function handleCreateUpdate(checkCall: CheckCallWithUpdate) {
  const travelOrder = props.resolveTravelOrder(checkCall);

  if (!travelOrder) {
    return;
  }

  emits("openCreateUpdate", travelOrder, checkCall);
}
</script>

<template>
  <omni-table
    :headers="tableHeaders"
    :items="checkCalls"
    :items-per-page="-1"
    :row-props="rowProps"
    :server-data="false"
    class="mt-5"
  >
    <template #[`header.locationAddress`]="{ column }: { column?: { title?: string | undefined } }">
      <div class="py-2">
        {{ column?.title }},
        <div>Miles left to next stop</div>
      </div>
    </template>

    <template #[`item.type`]="{ item }: { item: CheckCallWithUpdate }">
      <div>
        <span> {{ item.type }}</span>
        <dismissed-chip v-if="item.deletedBy" class="ml-3" />
      </div>
    </template>

    <template v-if="resolveTruckInfo" #[`item.truckNumber`]="{ item }: { item: CheckCallWithUpdate }">
      <template v-if="resolveTruckInfo(item)">
        <truck-link
          :truck-id="resolveTruckInfo(item)!.truckId"
          :truck-number="resolveTruckInfo(item)!.truckNumber"
          hide-label
        >
          <template #icon>
            <v-icon class="mr-1" size="small">mdi-truck-outline</v-icon>
          </template>
        </truck-link>

        <br />

        <span class="text-grey-darken-1 ml-1">
          (TO #{{ resolveTruckInfo(item)!.travelOrderId }}, Order #{{ resolveTruckInfo(item)!.orderNumber }})
        </span>
      </template>
    </template>

    <template #[`item.locationAddress`]="{ item }: { item: CheckCallWithUpdate }">
      {{ item.locationAddress }}
      <div class="font-size-12 text-grey-darken-1">
        Miles left to next stop: {{ filters.miles(item.nextStopDistance, 0) }}
      </div>
    </template>

    <template #[`item.dateTime`]="{ item }: { item: CheckCallWithUpdate }">
      <check-call-date-time :check-call="item" />
    </template>

    <template #[`item.brokerUpdate`]="{ item }: { item: CheckCallWithUpdate }">
      <p class="broker-update-message" v-html="item.update?.message"></p>
    </template>

    <template #[`item.actions`]="{ item }: { item: CheckCallWithUpdate }">
      <div v-if="item.update" class="broker-update-actions">
        <div class="d-flex align-center justify-space-between">
          <div class="font-weight-500 font-size-12 mr-2">
            Sent by <span class="text-primary">{{ item.update?.createdDispatcher.name }}</span>

            <br />
            <span class="text-grey-darken-1">
              at {{ getTime(item.update?.createdAt, dispatcher?.timezone) }}
              {{ dateTZ(item.createdAt, dispatcher!.timezone, "z") }}</span
            >
          </div>

          <v-btn
            color="primary"
            density="compact"
            icon="mdi-eye-outline"
            variant="text"
            @click="emits('viewUpdate', item)"
          ></v-btn>
        </div>
      </div>
      <permission-tooltip v-else :permission="DispatcherPermissionValue.CreateBrokerUpdate">
        <template #activator="{ disabled }">
          <v-btn
            :disabled="isCreateDisabled(item) || disabled"
            class="text-uppercase"
            color="primary"
            variant="flat"
            @click="handleCreateUpdate(item)"
          >
            Create update
          </v-btn>
        </template>

        {{ t("permission.create-broker-update") }}
      </permission-tooltip>
    </template>
  </omni-table>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

:deep(.omni-table) {
  table {
    table-layout: auto;
  }

  tbody td {
    width: 1%;
    white-space: nowrap;

    &:has(.broker-update-message) {
      width: auto;
      white-space: normal;
    }
  }
}

.broker-update-message {
  max-width: 500px;
  padding: 0 16px;
  white-space: pre-wrap;
}
</style>
